import { ChevronLeft, X } from 'lucide-react';
import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { problemMessage } from '../api/problem';
import { apiPlace, sectionPath } from '../place/place';
import { StateMessage } from '../ui/StateMessage';
import type { PeopleContext } from './PeoplePage';
import { PersonBody, type UserDetail } from './PersonBody';

/** What the sheet leaves in the history entry it closes to: whose row takes the focus back. */
export type SheetClosing = { readonly focusPerson: string };

type SheetState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly person: UserDetail }
  | { readonly status: 'error'; readonly code: unknown; readonly httpStatus: number | undefined };

function useEscapeToList(back: string, userId: string) {
  const navigate = useNavigate();

  /*
   * Esc closes the sheet, as it closes the area's panels (the standard's «`Esc` closes panels and
   * overlays»). Only a key pressed in the page itself counts: a menu, a select or a side panel open
   * above the sheet lives outside `.shell-content` and closes first, on its own Esc. Nor does a
   * key typed in a field, where Esc belongs to the field.
   */
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target !== document.body &&
        (target.closest('.shell-content') === null ||
          target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement)
      ) {
        return;
      }
      void navigate(back, { state: { focusPerson: userId } satisfies SheetClosing });
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, back, userId]);
}

function usePersonDetail(placeKind: 'tenants' | 'organizations', placeId: string, userId: string) {
  const [state, setState] = useState<SheetState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void apiClient
      .GET('/v1/administration/{placeKind}/{placeId}/users/{userId}', {
        params: { path: { placeKind, placeId, userId } },
      })
      .then(
        ({ data, error, response }) => {
          if (cancelled) {
            return;
          }
          setState(
            data !== undefined && response.ok
              ? { status: 'ready', person: data }
              : { status: 'error', code: error, httpStatus: response.status },
          );
        },
        () => {
          if (!cancelled) {
            setState({ status: 'error', code: null, httpStatus: undefined });
          }
        },
      );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, userId, attempt]);
  return { state, setAttempt };
}

function SheetBar({ back, closing }: { readonly back: string; readonly closing: SheetClosing }) {
  const { t } = useTranslation('administration');
  return (
    <div className="person-sheet-bar">
      {/* Below 900 px the sheet is the page, so it leads back; beside the list it closes. The
            wrappers carry the switch, because a button's own display utility would win over it. */}
      <span className="person-sheet-back">
        <Button asChild variant="ghost" size="sm">
          <Link to={back} state={closing}>
            <ChevronLeft />
            {t('person.back')}
          </Link>
        </Button>
      </span>
      <span className="person-sheet-close">
        <Button asChild variant="ghost" size="icon">
          <Link to={back} state={closing} aria-label={t('person.close')}>
            <X />
          </Link>
        </Button>
      </span>
    </div>
  );
}

function SheetBody({
  state,
  titleId,
  onRetry,
  body,
}: {
  readonly state: SheetState;
  readonly titleId: string;
  readonly onRetry: () => void;
  readonly body: (person: UserDetail) => ReactNode;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading') {
    return (
      <div>
        {/* The sheet's name while it loads stays outside the region, whose bones are hidden. */}
        <h2 id={titleId} className="sr-only">
          {t('common.loading')}
        </h2>
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-12 w-60" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </SkeletonRegion>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div>
        <h2 id={titleId} className="sr-only">
          {t('person.loadError')}
        </h2>
        <StateMessage
          role="alert"
          tone="error"
          icon={X}
          title={t('person.loadError')}
          text={problemMessage(t, state.code, state.httpStatus)}
          action={
            <Button variant="outline" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          }
        />
      </div>
    );
  }
  return body(state.person);
}

/**
 * One person, as seen from this place (FR-AD-4): their roles here and where each comes from, their
 * pending invitations here, and their last sign-in — and the acts this caller may use on them,
 * only those (FR-AD-3). A person who left the place while the sheet was open is said as such
 * (USER_NOT_IN_PLACE), not as a lost right.
 */
export function PersonSheet() {
  const { place, detail, reloadPeople, refreshPlace } = useOutletContext<PeopleContext>();
  const { userId = '' } = useParams();
  const [params] = useSearchParams();
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind ?? 'organizations';
  const placeId = pair?.placeId ?? '';
  const placeName = detail.place.name;
  const { state, setAttempt } = usePersonDetail(placeKind, placeId, userId);
  const titleId = useId();
  const back = `${sectionPath(place, 'people')}${params.size > 0 ? `?${params.toString()}` : ''}`;
  // Closing hands the list the person to give focus back to, whichever way the sheet closes.
  const closing: SheetClosing = { focusPerson: userId };

  useEscapeToList(back, userId);

  const reload = useCallback(() => {
    setAttempt((n) => n + 1);
    reloadPeople();
  }, [reloadPeople]);

  return (
    <aside aria-labelledby={titleId} className="person-sheet">
      <SheetBar back={back} closing={closing} />
      <SheetBody
        state={state}
        titleId={titleId}
        onRetry={() => setAttempt((n) => n + 1)}
        body={(person) => (
          <PersonBody
            person={person}
            titleId={titleId}
            placeKind={placeKind}
            placeId={placeId}
            placeName={placeName}
            currentPlace={place}
            onChanged={reload}
            onStanding={refreshPlace}
          />
        )}
      />
    </aside>
  );
}
